import { useState } from "react";
import type {
  LoadBreakdownData,
  LoadOpenItemsData,
  LoadSizeMixData,
  PartnerLoadKpiSummary,
  PartnerLoadRow,
  PartnerLoadTableData,
  TypeMixData
} from "@silph-scope/shared";
import { useDashboard, usePanel } from "../api/hooks";
import { usePresentScale } from "../hooks/usePresentMode";
import { useTheme } from "../theme/ThemeContext";
import { ChartPanel } from "../components/ChartPanel";
import { loadBreakdownOption, loadSizeMixOption, typeMixOption } from "../theme/chartOptions";
import { KpiCell, LoadOpenItemsTable, LoadPageHeader } from "./load/LoadShared";

const DASHBOARD_ID = "partner-load";

export function PartnerLoadPage() {
  const { theme } = useTheme();
  const presentScale = usePresentScale();
  const { data: dashboard } = useDashboard(DASHBOARD_ID);
  const interval = dashboard?.refreshInterval ?? "1h";

  const kpi = usePanel<PartnerLoadKpiSummary>(DASHBOARD_ID, "kpi-summary", interval);
  const breakdown = usePanel<LoadBreakdownData>(DASHBOARD_ID, "open-by-partner", interval);
  const typeMix = usePanel<TypeMixData>(DASHBOARD_ID, "type-mix", interval);
  const sizeMix = usePanel<LoadSizeMixData>(DASHBOARD_ID, "size-by-partner", interval);
  const partners = usePanel<PartnerLoadTableData>(DASHBOARD_ID, "partners", interval);
  const openItems = usePanel<LoadOpenItemsData>(DASHBOARD_ID, "open-items", interval);

  const [selected, setSelected] = useState<string | null>(null);
  const syncedAt = kpi.data?.syncedAt ? new Date(kpi.data.syncedAt) : null;
  const isStale = kpi.data?.status === "stale" || kpi.data?.status === "failed";

  return (
    <div className="task-ops">
      <LoadPageHeader
        dashboardId={DASHBOARD_ID}
        eyebrow="People"
        title="Partner Load"
        subtitle="Parsec Partner demand on the tracked roster — still-open work plus completions in the last 12 months."
        syncedAt={syncedAt}
        interval={interval}
        isFetching={kpi.isFetching}
      />

      {isStale && (
        <div className="stale-banner">
          <i className="ri-error-warning-fill" />
          Data may be out of date — last sync check failed or is stale.
        </div>
      )}

      {kpi.data && <PartnerKpiRow data={kpi.data.data} />}

      <section className="grid-2">
        <ChartPanel
          title="Open load by partner"
          subtitle="Top partners by open count"
          option={breakdown.data ? loadBreakdownOption(theme, breakdown.data.data, presentScale) : null}
        />
        <ChartPanel
          title="Open work by type"
          option={typeMix.data ? typeMixOption(theme, typeMix.data.data, presentScale) : null}
        />
      </section>

      <ChartPanel
        title="Open size mix by partner"
        subtitle="Top partners by open count"
        option={sizeMix.data ? loadSizeMixOption(theme, sizeMix.data.data, presentScale) : null}
      />

      <PartnersTable data={partners.data?.data} selected={selected} onSelect={setSelected} />
      <LoadOpenItemsTable
        data={openItems.data?.data}
        selected={selected}
        selectedKey="partner"
        onClear={() => setSelected(null)}
      />
    </div>
  );
}

function PartnerKpiRow({ data }: { data: PartnerLoadKpiSummary }) {
  return (
    <section className="dt-kpi-row">
      <KpiCell label="Partners" value={data.partners} />
      <KpiCell label="With open work" value={data.partnersWithOpen} />
      <KpiCell label="Open tasks" value={data.openTasks} />
      <KpiCell label="Backlog" value={data.backlog} sub="not overdue" />
      <KpiCell label="Overdue" value={data.overdue} valueClassName="is-bad" />
      <KpiCell label="Blocked" value={data.blocked} valueClassName="is-bad" />
      <KpiCell label="Open points" value={data.openPoints} sub="size weighted" />
      <KpiCell label="Closed" value={data.closedLast12Mo} sub="last 12 mo" valueClassName="is-ok" />
      <KpiCell label="Closure rate" value={`${data.closureRatePct}%`} />
      <KpiCell label="Avg days to close" value={data.avgDaysToClose} />
    </section>
  );
}

function PartnersTable({
  data,
  selected,
  onSelect
}: {
  data?: PartnerLoadTableData;
  selected: string | null;
  onSelect: (partner: string | null) => void;
}) {
  const rows = data?.rows ?? [];
  return (
    <div className="card table-panel">
      <div className="panel-header">
        <h2>Partners</h2>
        <span className="dt-filter-note">{rows.length} partners · click a row to filter open items</span>
      </div>
      <div className="dt-detail-scroll">
        <div className="tl-partner-header">
          <span>Partner</span>
          <span style={{ textAlign: "right" }}>Open</span>
          <span style={{ textAlign: "right" }}>Backlog</span>
          <span style={{ textAlign: "right" }}>Overdue</span>
          <span style={{ textAlign: "right" }}>Blocked</span>
          <span style={{ textAlign: "right" }}>Points</span>
          <span style={{ textAlign: "right" }}>Closed</span>
          <span style={{ textAlign: "right" }}>Closure</span>
          <span style={{ textAlign: "right" }}>Avg days</span>
          <span style={{ textAlign: "right" }}>Types</span>
          <span>Top type</span>
        </div>
        <div className="table-body">
          {rows.map((row) => (
            <PartnerRowView
              key={row.partner}
              row={row}
              selected={selected === row.partner}
              onSelect={onSelect}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function PartnerRowView({
  row,
  selected,
  onSelect
}: {
  row: PartnerLoadRow;
  selected: boolean;
  onSelect: (partner: string | null) => void;
}) {
  return (
    <div
      className={`tl-partner-row${selected ? " is-selected" : ""}`}
      onClick={() => onSelect(selected ? null : row.partner)}
    >
      <span className="task-title">{row.partner}</span>
      <span className="num">{row.open}</span>
      <span className="num">{row.backlog}</span>
      <span className={`num${row.overdue > 0 ? " is-bad" : ""}`}>{row.overdue}</span>
      <span className={`num${row.blocked > 0 ? " is-bad" : ""}`}>{row.blocked}</span>
      <span className="num">{row.openPoints}</span>
      <span className="num">{row.closed}</span>
      <span className="num">{row.closureRatePct}%</span>
      <span className="num">{row.avgDaysToClose ?? "—"}</span>
      <span className="num">{row.typeCount}</span>
      <span>{row.topType}</span>
    </div>
  );
}
